import { appRoutePath, appRoutePatterns } from './appRoutePatterns';
import { stripOfferDecodeJobPrefix } from '@/api/portfolioIdentityFormats';
import { normalizeInternalAppReturnPath } from '@/types/internalNavigation';

const SAFE_REF = /^[A-Za-z0-9._-]+$/;
export { normalizeInternalAppReturnPath } from '@/types/internalNavigation';

function safeValue(value: string | null, maxLength = 512): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed && trimmed.length <= maxLength ? trimmed : null;
}

function safeRefs(values: string[]): string[] {
  return [
    ...new Set(
      values.map((value) => safeValue(value)).filter((value): value is string => Boolean(value)),
    ),
  ];
}

/** Convert a stable public decode reference into the raw job id expected by APIs. */
export function normalizeOfferDecodeJobRef(value: string | null): string | null {
  const candidate = safeValue(value, 128);
  if (!candidate) return null;
  // The prefix is DERIVED from the one identity declaration in
  // forest_models/forest_models/contracts/portfolio_identity_formats.json — the
  // same template the gateway renders `jobRef` from.
  const raw = stripOfferDecodeJobPrefix(candidate);
  return raw && raw.length <= 128 && SAFE_REF.test(raw) ? raw : null;
}

/** What Stripe reported about the checkout the owner just came back from. */
export type CheckoutOutcome = 'success' | 'cancelled';

const CHECKOUT_OUTCOMES: readonly CheckoutOutcome[] = ['success', 'cancelled'];

function checkoutOutcome(value: string | null): CheckoutOutcome | null {
  const candidate = safeValue(value, 32);
  return CHECKOUT_OUTCOMES.find((outcome) => outcome === candidate) ?? null;
}

export interface LandingHandoffContext {
  propertyId: string | null;
  immutableContextRef: string | null;
  returnContextRef: string | null;
  returnTo: string | null;
  sourceSnapshotRefs: string[];
  scenarioRefs: string[];
  planId: string | null;
  planRevision?: string | null;
  revaluationRevision?: string | null;
  decodeJobId: string | null;
  section: string | null;
  skuId: string | null;
  /**
   * The `package` parameter both checkout producers write — the frontend's own
   * `settingsResultPath` and Django's `users/checkout_continuation.py`. An
   * unrecognised value parses to null so the settings page shows no outcome at
   * all rather than claiming one it cannot stand behind.
   */
  checkoutOutcome: CheckoutOutcome | null;
}

export interface SettingsContinuationLinks {
  returnUrl: string | null;
  checkoutSuccessPath: string;
  checkoutCancelPath: string;
  returnsToBrowse: boolean;
}

/**
 * Open Settings at one catalog-backed property offer while retaining the
 * immutable context and exact Price Estimate return owned by the caller.
 */
/** Settings section that holds the owner's subscription and the price catalog. */
export const SETTINGS_PACKAGES_SECTION = 'packages';

/** The in-app subscription and pricing destination, inside the app shell. */
export function settingsSubscriptionPath(): string {
  const target = new URL(appRoutePatterns.settings, 'https://www.lehti.ai');
  target.searchParams.set('section', SETTINGS_PACKAGES_SECTION);
  return `${target.pathname}${target.search}`;
}

export function buildSettingsCatalogHandoff(context: LandingHandoffContext): string {
  const propertyId = safeValue(context.propertyId, 128);
  const skuId = safeValue(context.skuId, 128);
  const returnTo = normalizeInternalAppReturnPath(context.returnTo);
  if (!propertyId || !skuId || !returnTo) {
    throw new Error('Settings catalog handoff requires a property, SKU, and return target.');
  }
  const parsedReturn = new URL(returnTo, 'https://www.lehti.ai');
  const expectedPath = appRoutePath(appRoutePatterns.priceEstimate, { propertyId });
  if (parsedReturn.pathname !== expectedPath) {
    throw new Error('Settings catalog handoff return target must be the owning valuation.');
  }
  const target = new URL(appRoutePatterns.settings, 'https://www.lehti.ai');
  appendImmutableContext(target.searchParams, { ...context, propertyId, skuId, returnTo });
  target.searchParams.set('return_to', returnTo);
  target.searchParams.set('section', SETTINGS_PACKAGES_SECTION);
  return `${target.pathname}${target.search}`;
}

function replaceRefs(
  params: URLSearchParams,
  key: 'source_snapshot_ref' | 'scenario_ref',
  values: readonly string[],
): void {
  const refs = new Set([...params.getAll(key), ...values]);
  params.delete(key);
  for (const ref of refs) params.append(key, ref);
}

function appendImmutableContext(params: URLSearchParams, context: LandingHandoffContext): void {
  if (context.propertyId) params.set('property', context.propertyId);
  if (context.immutableContextRef) {
    params.set('landing_context_ref', context.immutableContextRef);
  }
  if (context.returnContextRef) {
    params.set('return_context_ref', context.returnContextRef);
  }
  replaceRefs(params, 'source_snapshot_ref', context.sourceSnapshotRefs);
  replaceRefs(params, 'scenario_ref', context.scenarioRefs);
  if (context.planId) params.set('plan_id', context.planId);
  if (context.planRevision) params.set('plan_revision', context.planRevision);
  if (context.revaluationRevision) {
    params.set('revaluation_revision', context.revaluationRevision);
  }
  if (context.decodeJobId) params.set('decode_job_ref', context.decodeJobId);
  if (context.skuId) params.set('sku', context.skuId);
}

function relativeUrl(path: string, context: LandingHandoffContext): string {
  const url = new URL(path, 'https://www.lehti.ai');
  appendImmutableContext(url.searchParams, context);
  return `${url.pathname}${url.search}${url.hash}`;
}

function settingsResultPath(
  context: LandingHandoffContext,
  status: 'success' | 'cancelled',
): string {
  const url = new URL(appRoutePatterns.settings, 'https://www.lehti.ai');
  appendImmutableContext(url.searchParams, context);
  if (context.returnTo) url.searchParams.set('return_to', context.returnTo);
  url.searchParams.set('section', SETTINGS_PACKAGES_SECTION);
  url.searchParams.set('package', status);
  return `${url.pathname}${url.search}`;
}

/** One serializer for immediate return and checkout success/cancel continuity. */
export function buildSettingsContinuationLinks(
  context: LandingHandoffContext,
): SettingsContinuationLinks {
  const returnUrl = context.returnTo ? relativeUrl(context.returnTo, context) : null;
  return {
    returnUrl,
    checkoutSuccessPath: settingsResultPath(context, 'success'),
    checkoutCancelPath: settingsResultPath(context, 'cancelled'),
    returnsToBrowse: context.returnTo
      ? new URL(context.returnTo, 'https://www.lehti.ai').pathname === appRoutePatterns.browseProperties
      : false,
  };
}

/** One fail-closed parser shared by Landing and every owning destination. */
export function parseLandingHandoffContext(search: string): LandingHandoffContext {
  const params = new URLSearchParams(search);
  return {
    propertyId: safeValue(params.get('property'), 128),
    immutableContextRef: safeValue(params.get('landing_context_ref')),
    returnContextRef: safeValue(params.get('return_context_ref')),
    returnTo: normalizeInternalAppReturnPath(params.get('return_to')),
    sourceSnapshotRefs: safeRefs(params.getAll('source_snapshot_ref')),
    scenarioRefs: safeRefs(params.getAll('scenario_ref')),
    planId: safeValue(params.get('plan_id'), 256),
    planRevision: safeValue(params.get('plan_revision'), 256),
    revaluationRevision: safeValue(params.get('revaluation_revision'), 256),
    decodeJobId: normalizeOfferDecodeJobRef(
      params.get('offer_decode_job_id') ??
        params.get('decode_job_ref') ??
        params.get('decode_job_id'),
    ),
    section: safeValue(params.get('section'), 64),
    skuId: safeValue(params.get('sku'), 128),
    checkoutOutcome: checkoutOutcome(params.get('package')),
  };
}
